import { quoteIssueLabel } from './ui/badges-status.js';

export const emptyResultSummary = () => ({
  verified: 0,
  issues: 0,
  unresolved: 0,
  hallucinations: 0,
  quoteMismatches: 0,
});

const normalizeLabel = (value) => String(value || '').toLowerCase().replace(/-/g, '_');

const finalLabel = (result) => {
  if (!result || typeof result !== 'object') return null;
  return result?.congruence_result?.label
    || result?.congruence?.label
    || result?.analyze_result?.label
    || result?.analyze_result?.congruence_result?.label
    || (result?.label && (
      result.rationale !== undefined
      || result.confidence !== undefined
      || result.calibration !== undefined
      || result.status === 'error'
    ) ? result.label : null);
};

const looksLikeResult = (value) => !!value && typeof value === 'object' && (
  'congruence' in value
  || 'congruence_result' in value
  || 'analyze_result' in value
  || 'resolve' in value
  || 'resolve_result' in value
  || 'file_id' in value
  || 'method' in value
  || 'failure_reason' in value
  || finalLabel(value) !== null
);

export function categorizeResult(result) {
  const label = normalizeLabel(finalLabel(result));
  if (!label || ['pending', 'processing', 'parsing', 'resolving', 'analyzing', 'unknown'].includes(label)) {
    return null;
  }
  const verified = ['supports', 'partial', 'weak/qualified', 'quote_exact', 'quote_close'].includes(label);
  if (verified) return quoteIssueLabel(result) ? 'issues' : 'verified';
  if ([
    'hallucination', 'refutes', 'disputes', 'contradicts', 'unsupported', 'neutral',
    'quote_mismatch', 'quote_not_found', 'unrelated', 'mismatch',
  ].includes(label)) return 'issues';
  return 'unresolved';
}

/**
 * Summarize either the bridge payload ({footnotes, inline}) or the in-memory
 * ResultsCache map. Citation ids are de-duplicated across footnote/inline data.
 */
export function summarizeResults(payload = {}, jointResults = {}) {
  const summary = emptyResultSummary();
  const seen = new Set();
  const footnotes = payload instanceof Map ? Object.fromEntries(payload) : (payload.footnotes || payload);
  const inline = payload instanceof Map ? {} : (payload.inline || {});

  const tally = (citeId, result) => {
    let key = String(citeId ?? '');
    if (!key || key === '__error__' || seen.has(key)) return;
    const congruence = result?.congruence_result || result?.congruence || {};
    if (result?.joint_member_result || result?.joint_evaluation_group || congruence?.joint_member_result) {
      const group = result?.joint_group_result || congruence?.joint_group_result;
      if (!group) return;
      key = `joint:${group.group_id || result.joint_evaluation_group}`;
      if (seen.has(key)) return;
      result = group;
    }
    const category = categorizeResult(result);
    if (!category) return;
    seen.add(key);
    summary[category] += 1;
    const label = normalizeLabel(finalLabel(result));
    if (label === 'hallucination') summary.hallucinations += 1;
    if (quoteIssueLabel(result) || ['quote_mismatch', 'quote_not_found', 'quote_altered'].includes(label)) {
      summary.quoteMismatches += 1;
    }
  };

  for (const [outerKey, value] of Object.entries(footnotes || {})) {
    if (!value || typeof value !== 'object') continue;
    if (looksLikeResult(value)) tally(outerKey, value);
    else for (const [citeId, result] of Object.entries(value)) tally(citeId, result);
  }
  for (const pageData of Object.values(inline || {})) {
    if (!pageData || typeof pageData !== 'object') continue;
    const entries = Array.isArray(pageData)
      ? pageData.map((citation) => [citation?.id, citation])
      : Object.entries(pageData);
    for (const [citeId, result] of entries) tally(citeId, result);
  }
  const jointByFootnote = jointResults instanceof Map ? Object.fromEntries(jointResults) : jointResults;
  for (const groups of Object.values(jointByFootnote || {})) {
    for (const [groupId, group] of Object.entries(groups || {})) {
      tally(`joint:${groupId}`, group);
    }
  }
  return summary;
}

export const hasResultCounts = (summary) => !!summary && (
  (summary.verified || 0) > 0
  || (summary.issues || 0) > 0
  || (summary.unresolved || 0) > 0
);
